import { AppRoutes } from '@/constants/routes';
import { Permissions } from '@/constants/permissions';
import type { Guide } from './types';

/**
 * Restricting a GL account to particular locations (GAP-003).
 *
 * Its own section rather than a page inside the Chart of Accounts material, because the people who
 * need it arrive from two directions: an administrator setting the restriction up, and a branch
 * user who has just been refused and wants to know why. The second group outnumbers the first, so
 * the refusal messages are quoted verbatim and are searchable.
 */

export const accountLocationGuides: Guide[] = [
  // ── 1. What it is ──────────────────────────────────────────────────────────
  {
    slug: 'account-locations-overview',
    title: 'Restricting an account to certain locations',
    section: 'account-locations',
    level: 'Basics',
    summary:
      'Stop one branch posting into another branch’s till or bank account — how the restriction '
      + 'works, what it changes, and what it deliberately leaves alone.',
    menuPath: 'General Ledger → Chart of Accounts',
    screens: [
      { label: 'Chart of Accounts', route: AppRoutes.GL_COA,       permission: Permissions.GL_COA_VIEW },
      { label: 'Locations',         route: AppRoutes.MST_LOCATION, permission: Permissions.MST_LOCATION_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'Most GL accounts belong to the whole company — one accumulated depreciation account, one '
        + 'VAT payable account, used wherever they are needed. Some do not. A cash till sits in one '
        + 'shop. A branch bank account belongs to one branch. Posting to one of those from somewhere '
        + 'else does not produce a small labelling error: it puts money in the ledger where the '
        + 'money physically is not, and nobody finds out until someone counts the cash.' },

      { kind: 'para', text:
        'Tying an account to its locations closes that. Once an account names one or more locations, '
        + 'it disappears from the account picker everywhere else, and a transaction that reaches it '
        + 'anyway — by search, by typing a code, by import, or through the API — is refused.' },

      { kind: 'callout', tone: 'tip', title: 'Nothing changes until you say so',
        text:
          'Every account starts unrestricted, and stays that way until someone ticks a location on '
          + 'it. Restricting one account has no effect on any other. So this can be introduced one '
          + 'account at a time — usually the tills and branch bank accounts first — with everything '
          + 'else carrying on exactly as before.' },

      { kind: 'heading', text: 'What it is not' },
      { kind: 'table',
        headers: ['Setting', 'What it asks', 'Where it lives'],
        rows: [
          ['Permitted Locations',
           'WHICH locations may use this account at all',
           'Chart of Accounts → Org. Rules'],
          ['Location Mandatory',
           'Must a line posting here state a location?',
           'Chart of Accounts → Org. Rules'],
          ['User location access',
           'Which locations may this PERSON work at?',
           'Administration → Users → Access'],
        ] },
      { kind: 'para', text:
        'The three interact but answer different questions. An account can demand a location without '
        + 'restricting which ones; it can be restricted without being mandatory anywhere else; and a '
        + 'user’s own access decides which of the permitted locations they can actually reach.' },

      { kind: 'heading', text: 'What is covered' },
      { kind: 'list', items: [
        'The GL account picker on a journal line — restricted accounts are simply not offered.',
        'The “Pay from” list on payments and receipts, which was already location-aware.',
        'Saving a journal, and posting one — both are checked on the server, not just in the screen.',
        'Journals imported from Excel, which go through the same check as a typed one.',
        'Postings arriving from another system through the integration API.',
      ] },

      { kind: 'callout', tone: 'note', title: 'System-generated postings are not restricted',
        text:
          'Entries the system raises for you — a reversal, a year-end close, the GL side of a posted '
          + 'invoice — take their accounts from posting rules and control-account mappings rather '
          + 'than from someone picking one. Those are not gated, deliberately: a location restriction '
          + 'is a control on what people may choose, and blocking an automatic posting would stop '
          + 'work for a reason the operator could not act on.' },
    ],
    related: ['account-locations-setup', 'account-locations-refusals'],
  },

  // ── 2. Setting it up ───────────────────────────────────────────────────────
  {
    slug: 'account-locations-setup',
    title: 'Setting an account’s permitted locations',
    section: 'account-locations',
    level: 'Basics',
    summary:
      'Tick the locations an account belongs to, lift the restriction again, and see what the '
      + 'change looks like afterwards.',
    menuPath: 'General Ledger → Chart of Accounts',
    screens: [
      { label: 'Chart of Accounts', route: AppRoutes.GL_COA,       permission: Permissions.GL_COA_EDIT },
      { label: 'Locations',         route: AppRoutes.MST_LOCATION, permission: Permissions.MST_LOCATION_VIEW },
    ],
    blocks: [
      { kind: 'steps', steps: [
        { title: 'Open the account',
          where: 'General Ledger → Chart of Accounts',
          detail: 'Type its code in the CODE box, or pick it from the tree on the left.' },
        { title: 'Go to the Org. Rules tab, then Permitted Locations',
          detail:
            'Every active location in the company is listed, whether or not you personally work at '
            + 'it — this is where the restriction is maintained, so head office can grant a branch '
            + 'it does not post from.' },
        { title: 'Press Edit, then tick the locations this account belongs to',
          detail:
            'One is the common case — a till belongs to one shop. Tick several for an account two '
            + 'branches genuinely share. The line under the boxes tells you where things stand: '
            + '“Currently usable at 1 of 5 locations.”' },
        { title: 'Save',
          detail:
            'The restriction applies from the next transaction saved. Transactions already posted '
            + 'are untouched — they record what actually happened, and are never rewritten.' },
      ] },

      { kind: 'heading', text: 'Lifting the restriction' },
      { kind: 'para', text:
        'Clear every box, or press “Remove the restriction”, and save. The account goes back to being '
        + 'usable everywhere. Nothing is lost — the fact that it was restricted, by whom and when, '
        + 'stays in the audit trail.' },

      { kind: 'callout', tone: 'warn', title: 'A restricted account demands a location',
        text:
          'Once an account names locations, a journal line that names none is refused rather than '
          + 'allowed through. That is the point: if clearing the location field let the line past, '
          + 'the restriction would be a suggestion. Expect this on company-wide journals that used '
          + 'to leave the location blank — give them a location, or leave the account unrestricted.' },

      { kind: 'callout', tone: 'note', title: 'Restricting to a location you later deactivate',
        text:
          'The account stays restricted to it, which means nobody can use the account any more. '
          + 'That is correct — it is not an invitation to use the account everywhere — but it is '
          + 'worth remembering if an account suddenly disappears from every picker after a location '
          + 'is retired. Re-tick a live location to fix it.' },

      { kind: 'heading', text: 'Who may change this' },
      { kind: 'para', text:
        'Editing an account grants or revokes access to it, so it needs the same permission as any '
        + 'other change to the account, and it is recorded as a security event rather than an '
        + 'ordinary edit. Both the old set and the new one are kept, so “who opened this account up '
        + 'to Galle, and when?” has an answer.' },
    ],
    related: ['account-locations-overview', 'account-locations-refusals'],
  },

  // ── 3. Refusals ────────────────────────────────────────────────────────────
  {
    slug: 'account-locations-refusals',
    title: 'Why an account is missing, and what a refusal means',
    section: 'account-locations',
    level: 'Basics',
    summary:
      'The account you expected is not in the list, or the journal will not save — what each '
      + 'message is telling you and what to do about it.',
    menuPath: 'General Ledger',
    screens: [
      { label: 'GL Journals',       route: AppRoutes.GL_JOURNALS, permission: Permissions.GL_JOURNAL_VIEW },
      { label: 'Chart of Accounts', route: AppRoutes.GL_COA,      permission: Permissions.GL_COA_VIEW },
    ],
    blocks: [
      { kind: 'heading', text: '“The account is not in the picker”' },
      { kind: 'para', text:
        'The account picker on a journal line shows what may be used at the line’s location — the '
        + 'line’s own if it has one, otherwise the journal header’s. Change the location at the top '
        + 'of the journal and the list changes with it. If an account you expected is absent, the '
        + 'usual reason is that it is tied to a different location.' },
      { kind: 'para', text:
        'Searching does not get round it. Typing the code returns nothing at a location the account '
        + 'is not permitted at, and that is deliberate: a picker that filters but a search box that '
        + 'does not is a filter in name only.' },

      { kind: 'heading', text: 'The two refusals' },
      { kind: 'table',
        headers: ['Message', 'What happened', 'What to do'],
        rows: [
          ['Line 2: Account 1101 — Cash on Hand is not permitted at the selected location. It may only be used at: Main Office.',
           'The line points at an account tied to somewhere else.',
           'Either change the journal’s location to one the account allows, or use the account that belongs where you are.'],
          ['Line 2: Account 1101 — Cash on Hand is restricted to Main Office, so the line must say which location it belongs to.',
           'The account is restricted but the line names no location at all.',
           'Set the location on the journal header, or on that line.'],
        ] },
      { kind: 'para', text:
        'Both name the line number and the locations the account IS for, so the fix does not require '
        + 'opening the Chart of Accounts to find out.' },

      { kind: 'callout', tone: 'warn', title: 'A draft can be refused at posting',
        text:
          'The check runs again when the journal posts, not only when it is saved. A draft raised '
          + 'last week can name an account that has been tied to a different location since — '
          + 'posting is the moment the entry reaches the ledger, so it is the current rule that has '
          + 'to hold. The refusal names the line, and the draft is left as it was for you to fix.' },

      { kind: 'heading', text: 'Working across more than one branch' },
      { kind: 'para', text:
        'If your user account reaches several locations, the account list you see with no particular '
        + 'location chosen is everything any of your locations allows — you are not shown accounts '
        + 'that belong solely to a branch you cannot reach, and you cannot see one by naming that '
        + 'branch. Pick the location on the journal, and the list narrows to what may be used there.' },

      { kind: 'callout', tone: 'note', title: 'Still missing?',
        text:
          'Location is not the only thing that filters an account picker. A journal line offers '
          + 'posting accounts only, never group or header accounts; the “Pay from” list on a payment '
          + 'is filtered by the payment method and the voucher currency as well. If the account is '
          + 'unrestricted and still absent, the reason lies there.' },
    ],
    related: ['account-locations-overview', 'account-locations-setup', 'ap-payment-funding'],
  },
];
